import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "asvasa_admin_token";

export const adminApi = axios.create({ baseURL: API });

// Attach token on every request
adminApi.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// On 401, drop token + redirect
adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      if (typeof window !== "undefined" && !window.location.pathname.endsWith("/admin/login")) {
        window.location.href = "/admin/login";
      }
    }
    return Promise.reject(err);
  }
);

export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const login = (username, password) =>
  adminApi.post("/admin/login", { username, password }).then((r) => r.data);

export const fetchMe = () => adminApi.get("/admin/me").then((r) => r.data);
export const fetchStats = () => adminApi.get("/admin/stats").then((r) => r.data);

export const listProductsAdmin = () => adminApi.get("/admin/products").then((r) => r.data);
export const createProduct = (p) => adminApi.post("/admin/products", p).then((r) => r.data);
export const updateProduct = (slug, p) => adminApi.put(`/admin/products/${slug}`, p).then((r) => r.data);
export const deleteProduct = (slug) => adminApi.delete(`/admin/products/${slug}`).then((r) => r.data);

export const listBlogAdmin = () => adminApi.get("/admin/blog").then((r) => r.data);
export const createBlog = (p) => adminApi.post("/admin/blog", p).then((r) => r.data);
export const updateBlog = (slug, p) => adminApi.put(`/admin/blog/${slug}`, p).then((r) => r.data);
export const deleteBlog = (slug) => adminApi.delete(`/admin/blog/${slug}`).then((r) => r.data);

export const listCoupons = () => adminApi.get("/admin/coupons").then((r) => r.data);
export const createCoupon = (c) => adminApi.post("/admin/coupons", c).then((r) => r.data);
export const updateCoupon = (code, c) => adminApi.put(`/admin/coupons/${code}`, c).then((r) => r.data);
export const deleteCoupon = (code) => adminApi.delete(`/admin/coupons/${code}`).then((r) => r.data);

export const fetchSiteContent = () => adminApi.get("/admin/site-content").then((r) => r.data);
export const saveSiteContent = (content) =>
  adminApi.put("/admin/site-content", { content }).then((r) => r.data);
export const resetSiteContent = () =>
  adminApi.post("/admin/site-content/reset").then((r) => r.data);

export const listSubscribers = () => adminApi.get("/admin/subscribers").then((r) => r.data);

// ---------- Orders ----------
export const listOrders = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.status && params.status !== "all") qs.set("status", params.status);
  if (params.search) qs.set("search", params.search);
  const query = qs.toString() ? `?${qs}` : "";
  return adminApi.get(`/admin/orders${query}`).then((r) => r.data);
};
export const fetchOrderStats = () => adminApi.get("/admin/orders/stats").then((r) => r.data);
export const getOrder = (id) => adminApi.get(`/admin/orders/${id}`).then((r) => r.data);
export const updateOrder = (id, payload) => adminApi.patch(`/admin/orders/${id}`, payload).then((r) => r.data);
export const deleteOrder = (id) => adminApi.delete(`/admin/orders/${id}`).then((r) => r.data);
export const bulkDeleteOrders = (ids) =>
  adminApi.post(`/admin/orders/bulk-delete`, { ids }).then((r) => r.data);
export const renumberAllOrders = () =>
  adminApi.post(`/admin/orders/renumber`).then((r) => r.data);

// ---------- Shipping (Shiprocket) ----------
export const adminShipOrder = (id) => adminApi.post(`/admin/shipping/orders/${id}/ship`).then((r) => r.data);
export const adminCancelShipment = (id) => adminApi.post(`/admin/shipping/orders/${id}/cancel`).then((r) => r.data);
export const adminCreateReturn = (id) => adminApi.post(`/admin/shipping/orders/${id}/return`).then((r) => r.data);
export const adminRefreshTracking = (id) => adminApi.post(`/admin/shipping/orders/${id}/track`).then((r) => r.data);

// ---------- Backups ----------
export const listBackups = () => adminApi.get("/admin/backups").then((r) => r.data);
export const runBackup = () => adminApi.post("/admin/backups/run").then((r) => r.data);
export const restoreBackup = (url) =>
  adminApi.post("/admin/backups/restore", { url }).then((r) => r.data);

// ---------- Categories ----------
export const listCategoriesAdmin = () => adminApi.get("/admin/categories").then((r) => r.data);
export const createCategory = (payload) => adminApi.post("/admin/categories", payload).then((r) => r.data);
export const updateCategoryAdmin = (slug, payload) => adminApi.put(`/admin/categories/${slug}`, payload).then((r) => r.data);
export const deleteCategoryAdmin = (slug) => adminApi.delete(`/admin/categories/${slug}`).then((r) => r.data);

// ---------- Reviews ----------
export const listReviewsAdmin = (status) =>
  adminApi.get(`/admin/reviews${status ? `?status=${status}` : ""}`).then((r) => r.data);
export const moderateReview = (id, approved) =>
  adminApi.patch(`/admin/reviews/${id}`, { approved }).then((r) => r.data);
export const deleteReviewAdmin = (id) => adminApi.delete(`/admin/reviews/${id}`).then((r) => r.data);

// ---------- CSV import ----------
export const importProductsCsv = (file) => {
  const form = new FormData();
  form.append("file", file);
  return adminApi.post("/admin/products/import-csv", form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// ---------- Analytics ----------
export const fetchSalesAnalytics = (days = 30) =>
  adminApi.get(`/admin/analytics/sales?days=${days}`).then((r) => r.data);
export const adminRefund = (id, amount = null) =>
  adminApi.post(`/admin/payments/refund/${id}`, amount ? { amount } : {}).then((r) => r.data);
